import { Alert, Button, Input, InputNumber, Select, Space, Switch, Table, Tag, Typography, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { platformListRateCatalog, platformUpdateRateCatalogEntry } from '../api'
import type { RateCatalogEntryRow } from '../types'
import { SectionCard, formatDateTime } from './adminShared'

/**
 * VISTOERA_AI_BILLING_PHASE2_FULL_RATE_CATALOG_USAGE_LEDGER_AND_PRICING_V1
 * (task spec §25) — Platform Admin "AI 计费规则" tab for the fine-grained
 * Rate Catalog V2 (`saas_rate_catalog`), sibling to (never replacing) the
 * legacy 4-row "费率与套餐" page's Rate Rules table.
 *
 * Columns per spec: 分类 / 名称 / Operation Key / Provider / Model / 计费
 * 单位 / 积分-单位 / 预计成本-单位 / 状态 / 更新时间 / 更新人, with
 * Category/Provider/Model/Enabled filters (task spec §25). Edits go
 * through `PATCH /platform/rate-catalog/{id}` — every save writes an
 * `AdminAuditLog` row server-side (see `saas_platform.py::
 * update_rate_catalog_entry`) and NEVER rewrites any already-written
 * `CreditLedgerEntry.rate_snapshot` (task spec §28 — historical snapshot
 * immutability), which is why the same warning banner as the legacy Rate
 * Rules page is shown here too.
 *
 * `estimated_cost_per_unit`/`cost_currency` ARE rendered here (this is a
 * Platform-Admin-only route/page) but must never be surfaced on any
 * tenant-facing surface (task spec §26).
 */
export default function AIBillingRulesPage() {
  const [rows, setRows] = useState<RateCatalogEntryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [draftPoints, setDraftPoints] = useState<Record<string, number>>({})

  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined)
  const [providerFilter, setProviderFilter] = useState<string | undefined>(undefined)
  const [modelFilter, setModelFilter] = useState<string | undefined>(undefined)
  const [enabledFilter, setEnabledFilter] = useState<boolean | undefined>(undefined)
  const [searchText, setSearchText] = useState('')

  const load = () => {
    setLoading(true)
    setError(null)
    platformListRateCatalog()
      .then((res) => setRows(res.data))
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const categoryOptions = useMemo(() => uniqueSorted(rows.map((r) => r.category_key)), [rows])
  const providerOptions = useMemo(() => uniqueSorted(rows.map((r) => r.provider_key)), [rows])
  const modelOptions = useMemo(() => uniqueSorted(rows.map((r) => r.model_key)), [rows])

  const filteredRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase()
    return rows.filter((r) => {
      if (categoryFilter && r.category_key !== categoryFilter) return false
      if (providerFilter && r.provider_key !== providerFilter) return false
      if (modelFilter && r.model_key !== modelFilter) return false
      if (enabledFilter !== undefined && r.enabled !== enabledFilter) return false
      if (
        needle &&
        !r.display_name.toLowerCase().includes(needle) &&
        !r.operation_key.toLowerCase().includes(needle)
      ) {
        return false
      }
      return true
    })
  }, [rows, categoryFilter, providerFilter, modelFilter, enabledFilter, searchText])

  const savePoints = async (row: RateCatalogEntryRow) => {
    const draft = draftPoints[row.id]
    if (draft === undefined) return
    setSavingId(row.id)
    try {
      const res = await platformUpdateRateCatalogEntry(row.id, { points_per_unit: draft })
      setRows((prev) => prev.map((r) => (r.id === row.id ? res.data : r)))
      setDraftPoints((d) => {
        const next = { ...d }
        delete next[row.id]
        return next
      })
      message.success('积分已更新')
    } catch (err) {
      message.error((err as Error).message)
    } finally {
      setSavingId(null)
    }
  }

  const toggleEnabled = async (row: RateCatalogEntryRow, enabled: boolean) => {
    try {
      const res = await platformUpdateRateCatalogEntry(row.id, { enabled })
      setRows((prev) => prev.map((r) => (r.id === row.id ? res.data : r)))
    } catch (err) {
      message.error((err as Error).message)
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          AI 计费规则
        </Typography.Title>
        <Typography.Text type="secondary">
          全量 operation / provider / model 计费单价目录（INITIAL_TEST_PRICING_V1 测试期定价，可人工调整）。
        </Typography.Text>
      </div>

      {error && <Alert type="error" message={error} showIcon style={{ margin: '12px 0' }} />}

      <SectionCard title="AI 计费规则（Rate Catalog）">
        <Alert
          type="info"
          showIcon
          style={{ margin: '4px 4px 8px' }}
          message="修改仅影响未来产生的计费，不会追溯影响历史积分流水（rate_snapshot 已冻结）。预计成本仅平台管理员可见，租户侧不可见。"
        />
        <Space wrap style={{ margin: '4px 4px 12px' }}>
          <Select
            allowClear
            placeholder="分类"
            style={{ width: 160 }}
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categoryOptions.map((v) => ({ label: v, value: v }))}
          />
          <Select
            allowClear
            placeholder="Provider"
            style={{ width: 160 }}
            value={providerFilter}
            onChange={setProviderFilter}
            options={providerOptions.map((v) => ({ label: v, value: v }))}
          />
          <Select
            allowClear
            placeholder="Model"
            style={{ width: 180 }}
            value={modelFilter}
            onChange={setModelFilter}
            options={modelOptions.map((v) => ({ label: v, value: v }))}
          />
          <Select
            allowClear
            placeholder="状态"
            style={{ width: 120 }}
            value={enabledFilter}
            onChange={setEnabledFilter}
            options={[
              { label: '已启用', value: true },
              { label: '已停用', value: false },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="搜索名称 / Operation Key"
            style={{ width: 240 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Space>
        <Table
          className="pf-data-table"
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={filteredRows}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          columns={[
            { title: '分类', dataIndex: 'category_key', width: 110 },
            { title: '名称', dataIndex: 'display_name', width: 220 },
            { title: 'Operation Key', dataIndex: 'operation_key', width: 220, render: (v: string) => <code>{v}</code> },
            { title: 'Provider', dataIndex: 'provider_key', width: 110 },
            { title: 'Model', dataIndex: 'model_key', width: 140 },
            { title: '计费单位', dataIndex: 'billing_unit', width: 150 },
            {
              title: '积分 / 单位',
              dataIndex: 'points_per_unit',
              width: 160,
              render: (v: string | null, row) => (
                <Space>
                  <InputNumber
                    size="small"
                    min={0}
                    step={0.5}
                    value={draftPoints[row.id] ?? (v === null ? undefined : Number(v))}
                    onChange={(val) => setDraftPoints((d) => ({ ...d, [row.id]: val ?? 0 }))}
                    style={{ width: 90 }}
                  />
                  <Button
                    size="small"
                    type="primary"
                    loading={savingId === row.id}
                    disabled={draftPoints[row.id] === undefined || draftPoints[row.id] === Number(v)}
                    onClick={() => savePoints(row)}
                  >
                    保存
                  </Button>
                </Space>
              ),
            },
            {
              title: '预计成本 / 单位',
              dataIndex: 'estimated_cost_per_unit',
              width: 140,
              render: (v: string | null, row) => (v === null ? '—' : `${v} ${row.cost_currency ?? ''}`),
            },
            {
              title: '状态',
              dataIndex: 'enabled',
              width: 150,
              render: (v: boolean, row) => (
                <Space>
                  <Switch checked={v} onChange={(checked) => toggleEnabled(row, checked)} />
                  <Tag color={row.pricing_status === 'ACTIVE' ? 'green' : 'orange'}>{row.pricing_status}</Tag>
                </Space>
              ),
            },
            { title: '更新时间', dataIndex: 'updated_at', width: 160, render: (v: string) => formatDateTime(v) },
            { title: '更新人', dataIndex: 'updated_by', width: 140, render: (v: string | null) => v ?? '—' },
          ]}
        />
      </SectionCard>
    </div>
  )
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values)).sort()
}
